import { useAppLogoAsset } from "@shared/hooks/useAppLogoAsset";
import { useAuth } from "@shared/hooks/useAuth";
import { Typography } from "@mui/material";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import { Form } from "@shared/components/Form";
import { TextField } from "@shared/components/TextField";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { AuthContainer, ErrorMessage } from "./Auth.styles";
import { useForgotPassword } from "@queries/auth/useForgotPassword";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  ForgotPasswordFormSchema,
  forgotPasswordFormSchema,
  FORGOT_PASSWORD_SCHEMA_DEFAULT_VALUES,
} from "./auth-form-helpers";
import { toast } from "@shared/services/toast";
import { SubmitButton } from "@avenue-8/sidekick-ui";

type Props = {
  email?: string | null;
  setEmailResetPassword: (email: string) => void;
};

export const StartForgotPassword = (props: Props) => {
  const { email, setEmailResetPassword } = props;
  const { src: logoSrc, alt: logoAlt } = useAppLogoAsset();
  const { error } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const {
    register,
    formState: { errors },
    handleSubmit,
    getValues,
  } = useForm<ForgotPasswordFormSchema>({
    defaultValues: {
      ...FORGOT_PASSWORD_SCHEMA_DEFAULT_VALUES,
    },
    resolver: yupResolver(forgotPasswordFormSchema),
    reValidateMode: "onChange",
  });
  const loginPath = location.search ? `/login${location.search}` : "/login";

  const forgotPasswordMutation = useForgotPassword({
    onSuccess: (data) => {
      const email = getValues("email");
      if (data.resetFlow === "REGENERATE_TEMPORARY_PASSWORD") {
        navigate(loginPath, { state: { email }, replace: true });
        toast.success(
          <p>
            A temporary password has been sent to <strong>{email}</strong>. If
            you do not see the email in your inbox, please also check your spam
            or junk folder to ensure you receive it.
          </p>,
          {
            autoClose: 15000,
            toastId: "regenerate-temporary-password",
            icon: <MailOutlineIcon />,
            theme: "light",
          },
        );
        return;
      }
      if (data.resetFlow === "START_RESET_PASSWORD") {
        setEmailResetPassword(getValues("email"));
        return;
      }
      toast.error("Invalid reset password request.", { theme: "light" });
    },
    onError: () => {
      toast.error("Failed to start forgot password.", { theme: "light" });
    },
  });

  const handleForgotPassword = (data: ForgotPasswordFormSchema) => {
    forgotPasswordMutation.mutate(data);
  };

  return (
    <AuthContainer>
      <header>
        <img src={logoSrc} alt={logoAlt} />
        <Typography mt={1} mb={2}>
          Forgot your password? Enter your email below and we&apos;ll send you a
          link
        </Typography>
      </header>

      <Form
        onSubmit={handleSubmit(handleForgotPassword)}
        aria-disabled={forgotPasswordMutation.isPending}
      >
        {error && <ErrorMessage>{error.message}</ErrorMessage>}
        <TextField
          name={"email"}
          label={"Email"}
          placeholder={"Email"}
          inputProps={{ ...register("email") }}
          fieldError={errors.email}
          defaultValue={email}
          autoFocus
        />
        <SubmitButton isLoading={forgotPasswordMutation.isPending}>
          Reset my password
        </SubmitButton>
        <Typography textAlign={"center"} mt={1}>
          <Link to={loginPath}>Back to sign in</Link>
        </Typography>
      </Form>
    </AuthContainer>
  );
};
